/**
 * 导游相关接口
 * 对应 Apipost: 用户端 > 导游
 */

import { http, peiwantHttp } from "@/http/axios";

// ==================== 类型定义 ====================

/** 导游列表项 */
export interface GuideListItem {
  id: number;
  name: string;
  avatar: string;
  gender: number;
  gender_text: string;
  level: number;
  level_text: string;
  languages: string;
  service_area: string;
  specialty: string;
  rating: number | null;
  comment_count: number;
  daily_fee: string;
  service_type: number;
  service_type_text: string;
  max_people: number;
  introduction: string;
}

/** 导游列表响应 */
export interface GuideListResponse {
  code: number;
  message: string;
  data: {
    total: number;
    page: number;
    limit: number;
    list: GuideListItem[];
  };
}

/** 导游详情项 */
export interface GuideDetailItem {
  id: number;
  name: string;
  avatar: string;
  gender: number;
  gender_text: string;
  age: number | null;
  birthplace: string;
  email: string;
  level: number;
  level_text: string;
  languages: string;
  service_area: string;
  specialty: string;
  introduction: string;
  work_history: string | null;
  honors: string | null;
  rating: number | null;
  comment_count: number;
  daily_fee: string;
  service_type: number;
  service_type_text: string;
  max_people: number;
  cancel_policy: string | null;
  qualification: Array<{ type: string; type_text: string; number?: string | null; image?: string | null; expire_date?: string | null }>;
  insurance_status: number | null;
  merchant: string | null;
}

/** 导游详情响应 */
export interface GuideDetailResponse {
  code: number;
  message: string;
  data: GuideDetailItem;
}

/** 等级选项 */
export interface LevelOption {
  value: number;
  label: string;
}

/** 服务类型选项 */
export interface ServiceTypeOption {
  value: number;
  label: string;
}

/** 搜索条件响应 */
export interface GuideOptionsResponse {
  code: number;
  message: string;
  data: {
    cities: string[];
    languages: string[];
    levels: LevelOption[];
    service_types: ServiceTypeOption[];
  };
}
// ==================== 接口调用 ====================

/**
 * 接口1：导游列表
 * GET /home/guide/list
 * 支持分页、城市、语言、等级筛选和排序
 */
export async function fetchGuideList(params: {
  page?: number;
  limit?: number;
  city?: string;
  language?: string;
  sort?: string;
  service_type?: number | string;
}): Promise<GuideListResponse["data"]> {
  console.log("[API] 获取导游列表:", "/home/guide/list", params);

  const res = await http.get<GuideListResponse>("/home/guide/list", { params });

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取导游列表失败");
  }

  return res.data.data;
}

/**
 * 接口2：导游详情
 * GET /home/guide/detail?id={id}
 */
export async function fetchGuideDetail(id: number): Promise<GuideDetailResponse["data"]> {
  console.log("[API] 获取导游详情:", "/home/guide/detail", { id });

  const res = await http.get<GuideDetailResponse>("/home/guide/detail", { params: { id } });

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取导游详情失败");
  }

  return res.data.data;
}

/**
 * 接口3：导游搜索条件
 * GET /home/guide/options
 * 获取城市、语言、等级、服务类型的筛选选项
 */
export async function fetchGuideOptions(): Promise<GuideOptionsResponse["data"]> {
  console.log("[API] 获取导游搜索条件:", "/home/guide/options");

  const res = await http.get<GuideOptionsResponse>("/home/guide/options");

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "获取导游搜索条件失败");
  }

  return res.data.data;
}

// ==================== 达人详情（/api/getStaffInfo）====================

/** 后端原始返回的达人详情 */
export interface RawStaffDetail {
  id: number;
  staff_cate_id: number;
  company_id: number;
  title: string; // 称呼
  name: string; // 姓名
  login_name: string; // 登录帐号
  thumb: string; // 头像
  imgs: string[]; // 图片数组
  phone: string; // 联系电话
  sex: number; // 性别 0未知 1男 2女
  sort: number;
  is_recommend: number;
  status: number;
  is_del: number;
  delete_time: number;
  birthday: string; // 生日
  work_time: string;
  resume: string; // 个人履历/介绍
  start_time: string; // 开始时间
  end_time: string; // 结束时间
  week_str: string; // 工作日
  latitude: string;
  longitude: string;
  service_radius: string; // 服务半径
  service_score: string; // 信誉分
  fee_rate: string; // 提成比例
  add_time: number;
  place: string; // 工作地址
  remind_uid: number;
  qualification: unknown[]; // 证书1
  qualification_b: unknown[]; // 证书2
  health_pic: string[]; // 健康证图片
  emergency_contact: string; // 紧急联系人
  emergency_phone: string; // 紧急联系人电话
  service_label: string; // 服务标签JSON字符串
  current_latitude: string;
  current_longitude: string;
  current_address: string; // 当前位置
  current_time: number;
  city_id: number;
  work_status: number; // 0停止接单 1可接单
  score: string; // 评分
  relationship: string; // 情感情况
  education: string; // 学历
  star_signs: string; // 星座
  staff_height: number; // 身高cm
  staff_weight: number; // 体重kg
  content_audio: string; // 语音介绍
  customer_service_uid: number;
  type: number;
  staff_cate_name: string;
  city_name: string | null;
  distance: string; // 距离(km)
  collect_count: number; // 关注数
  like_count: number; // 点赞数
  order_count: number; // 订单数
  auth: string; // 证书上传情况
  age: number; // 年龄
  agent_name: string; // 所属代理商
}

/** 达人详情响应 */
interface StaffDetailResponse {
  status: number;
  msg: string;
  data: RawStaffDetail;
}

/** 解析后的达人详情（供页面使用） */
export interface StaffDetail {
  id: number;
  name: string;
  nickname: string;
  avatar: string;
  coverImage: string;
  images: string[];
  badge: string;
  distance: string;
  city: string;
  rating: string;
  score: string;
  stats: {
    following: number;
    followers: number;
    servedCount: number;
  };
  certifications: string[];
  healthTag: string | null;
  introduction: string;
  resume: string;
  sex: number;
  age: number;
  birthday: string;
  education: string;
  relationship: string;
  starSigns: string;
  height: number;
  weight: number;
  serviceLabel: string[];
  workStatus: number; // 0停止 1可接单
  workTime: { start: string; end: string };
  weekStr: string;
  place: string;
  serviceRadius: string;
  serviceScore: string;
  phone: string;
  isRecommend: number;
  status: number;
  staffCateId: number;
  staffCateName: string;
  collectCount: number;
  likeCount: number;
  orderCount: number;
  commentCount: number; // 评价数
  auth: string;
  qualification: unknown[];
  healthPic: string[];
  longitude: string;
  latitude: string;
  currentLongitude: string;
  currentLatitude: string;
  currentAddress: string;
  contentAudio: string;
}

/**
 * 将后端原始详情数据转换为页面使用的格式
 */
function normalizeStaffDetail(raw: RawStaffDetail): StaffDetail {
  const serviceLabel: string[] = [];
  try {
    const parsed = JSON.parse(raw.service_label || "[]");
    if (Array.isArray(parsed)) serviceLabel.push(...parsed);
  } catch { /* ignore */ }

  return {
    id: raw.id,
    name: raw.name || raw.title || "",
    nickname: raw.name || raw.title || "",
    avatar: raw.thumb || "",
    coverImage: raw.imgs?.[0] || raw.thumb || "",
    images: raw.imgs || [],
    badge: raw.staff_cate_name || "",
    distance: raw.distance ? `${raw.distance}km` : "",
    city: raw.city_name || raw.place?.split(/[市区县]/)[0] || "",
    rating: raw.score || "-",
    score: raw.score || "-",
    stats: {
      following: raw.collect_count || 0,
      followers: 0,
      servedCount: raw.order_count || 0,
    },
    certifications: buildCertList(raw),
    healthTag: raw.health_pic?.length > 0 ? "已上传健康证明" : null,
    introduction: raw.resume || "",
    resume: raw.resume || "",
    sex: raw.sex ?? 0,
    age: raw.age ?? 0,
    birthday: raw.birthday || "",
    education: raw.education || "",
    relationship: raw.relationship || "",
    starSigns: raw.star_signs || "",
    height: raw.staff_height || 0,
    weight: raw.staff_weight || 0,
    serviceLabel,
    workStatus: raw.work_status ?? 0,
    workTime: { start: raw.start_time || "", end: raw.end_time || "" },
    weekStr: raw.week_str || "",
    place: raw.place || "",
    serviceRadius: raw.service_radius || "",
    serviceScore: raw.service_score || "",
    phone: raw.phone || "",
    isRecommend: raw.is_recommend ?? 0,
    status: raw.status ?? 1,
    staffCateId: raw.staff_cate_id,
    staffCateName: raw.staff_cate_name || "",
    collectCount: raw.collect_count || 0,
    likeCount: raw.like_count || 0,
    orderCount: raw.order_count || 0,
    commentCount: raw.comment_count || 0,
    auth: raw.auth || "",
    qualification: raw.qualification || [],
    healthPic: raw.health_pic || [],
    longitude: raw.longitude || "",
    latitude: raw.latitude || "",
    currentLongitude: raw.current_longitude || "",
    currentLatitude: raw.current_latitude || "",
    currentAddress: raw.current_address || "",
    contentAudio: raw.content_audio || "",
  };
}

/** 构建认证标签列表 */
function buildCertList(raw: RawStaffDetail): string[] {
  const certs: string[] = [];
  if (raw.auth === "all") certs.push("认证齐全");
  if (raw.qualification?.length > 0) certs.push("资质证书");
  if (raw.qualification_b?.length > 0) certs.push("专业证书");
  if (raw.health_pic?.length > 0) certs.push("健康认证");
  if (certs.length === 0) {
    if (raw.phone) certs.push("实名认证");
  }
  return certs.length > 0 ? certs : ["已认证"];
}

/**
 * 达人详情
 * GET /peiwant/api/getStaffInfo?staff_id={id}&longitude={lng}&latitude={lat}
 * 域名：https://peiwan.banly.com.cn（达人端）
 * 通过 /peiwant 代理转发
 */
export async function fetchStaffDetail(params: {
  staff_id: number | string;
  longitude?: string;
  latitude?: string;
}): Promise<StaffDetail> {
  console.log("[API] 获取达人详情:", "/peiwant/api/getStaffInfo", params);

  const res = await peiwantHttp.get<StaffDetailResponse>("/api/getStaffInfo", {
    params: {
      ...params,
    },
  });

  if (res.data.status !== 200) {
    throw new Error(res.data.msg || "获取达人详情失败");
  }

  return normalizeStaffDetail(res.data.data);
}

// ==================== 达人评论相关接口 ====================

/** 评论Tab项 */
export interface CommentTabItem {
  value: number; // 0全部 5好评 3中评 1差评 4图片
  label: string;
  count: number;
}

/** 评论Tabs响应 */
interface CommentTabsResponse {
  status: number;
  msg: string;
  data: CommentTabItem[];
}

/** 原始评论项 */
interface RawCommentItem {
  id: number;
  uid: number;
  order_id: number;
  service_staff_id: number;
  content: string;
  images: string[];
  status: number;          // 0未审核 10通过 20未通过
  service_score: string;   // 星级 "3.00"
  is_del: number;
  delete_time: number;
  add_time: string;
  reply: string | null;    // 商家回复
  service_label: string;
  nickname: string;
  avatar: string;
  item_name: string;       // 服务项目名
  city: string;
  district: string;
}

/** 评论列表响应 */
interface StaffCommentResponse {
  status: number;
  msg: string;
  data: {
    list: RawCommentItem[];
    count: number;
  };
}

/** 解析后的评论项（供页面使用） */
export interface CommentItem {
  id: number;
  uid: number;
  nickname: string;
  avatar: string;
  score: number;           // 1-5 星（从 service_score 解析）
  content: string;
  images: string[];
  serviceName: string;     // 服务项目名
  city: string;
  district: string;
  reply?: string;          // 商家回复
  createTime: string;
  orderId: number;
}

/**
 * 获取评论Tab数量统计
 * GET /peiwant/api/getCommentTabs?service_staff_id={id}
 */
export async function fetchCommentTabs(staffId: number | string): Promise<CommentTabItem[]> {
  console.log("[API] 获取评论tabs:", "/peiwant/api/getCommentTabs", { staffId });

  const res = await peiwantHttp.get<CommentTabsResponse>("/api/getCommentTabs", {
    params: { service_staff_id: staffId },
  });

  if (res.data.status !== 200) {
    throw new Error(res.data.msg || "获取评论分类失败");
  }

  return res.data.data;
}

/**
 * 获取达人评论列表
 * GET /peiwant/api/getStaffComment?service_staff_id={id}&page=1&limit=15&service_score=0
 * @param service_score - 评论星级：0全部，5好评，3中评，1差评，4图片
 */
export async function fetchStaffCommentList(params: {
  service_staff_id: number | string;
  page?: number;
  limit?: number;
  service_score?: string | number;
}): Promise<{ list: CommentItem[]; count: number }> {
  console.log("[API] 获取达人评论:", "/peiwant/api/getStaffComment", params);

  const res = await peiwantHttp.get<StaffCommentResponse>("/api/getStaffComment", {
    params: {
      page: 1,
      limit: 15,
      service_score: 0,
      ...params,
    },
  });

  if (res.data.status !== 200) {
    throw new Error(res.data.msg || "获取评论列表失败");
  }

  return {
    list: res.data.data.list.map((raw) => ({
      id: raw.id,
      uid: raw.uid,
      nickname: raw.nickname,
      avatar: raw.avatar,
      score: Math.round(parseFloat(raw.service_score) || 0),
      content: raw.content,
      images: raw.images || [],
      serviceName: raw.item_name || "",
      city: raw.city || "",
      district: raw.district || "",
      reply: raw.reply || undefined,
      createTime: raw.add_time,
      orderId: raw.order_id,
    })),
    count: res.data.data.count,
  };
}

// ==================== 达人列表（/api/staff_page）====================

/** 标签项 */
interface LabelItem {
  label_id: number;
  type: number;
  name: string;
}

/** 用户信息嵌套结构 */
interface StaffUserInfo {
  uid: number | null;
  avatar: string;
  nickname: string;
  sex: number;
  is_vip: number;
  label_list: LabelItem[];
  type: number;
  staff_info: {
    staff_id: number;
    nickname: string;
    avatar: string;
    staff_cate_id: number;
    staff_cate_name: string;
  };
}

/** 达人列表项 */
export interface StaffItem {
  staff_id: number;
  staff_cate_id: number;
  name: string; // 简介
  work_status: number; // 0 停止接单 1 可接单
  uid: number | null;
  love_num: number | null;
  longitude: string;
  latitude: string;
  image: string; // 封面图
  city: string;
  user_info: StaffUserInfo;

  // 兼容旧字段映射（页面组件使用这些字段名）
  id: number;
  avatar: string;
  nickname: string;
  service_label: string[];
  introduction: string;
  score: string;
  served_count: number;
  city_name: string;

  /** 分类 ID（用于筛选） */
  cate_id: number;
  /** 分类名称 */
  cate_name: string;
}

/** 达人列表响应 */
export interface StaffItemListResponse {
  status: number;
  msg: string;
  data: {
    list: RawStaffItem[];
    count: number;
  };
}

/** 后端原始返回的达人项 */
interface RawStaffItem {
  staff_id: number;
  staff_cate_id: number;
  name: string;
  work_status: number;
  uid: number | null;
  love_num: number | null;
  longitude: string;
  latitude: string;
  image: string;
  city: string;
  user_info: StaffUserInfo;
}

/**
 * 将后端原始数据转换为页面使用的格式
 */
function normalizeStaffItem(raw: RawStaffItem): StaffItem {
  return {
    ...raw,
    id: raw.staff_id,
    avatar: raw.user_info?.avatar || raw.image,
    nickname: raw.user_info?.nickname || "",
    service_label: raw.user_info?.label_list?.map((l) => l.name) || [],
    introduction: raw.name || "",
    score: "",
    served_count: 0,
    city_name: raw.city || raw.user_info?.staff_info?.staff_cate_name || "",
    cate_id: raw.staff_cate_id,
    cate_name: raw.user_info?.staff_info?.staff_cate_name || "",
  };
}

/**
 * 达人列表
 * GET /peiwant/api/staff_page?search=&category_id=0&page=1&limit=15
 * 域名：https://peiwan.banly.com.cn（达人端）
 * 通过 /peiwant 代理转发，其他模块的 /api 不受影响
 */
export async function fetchStaffItemList(params?: {
  search?: string;
  category_id?: number | string;
  page?: number;
  limit?: number;
}): Promise<{ list: StaffItem[]; count: number }> {
  console.log("[API] 获取达人列表:", "/peiwant/api/staff_page", params);

  const res = await peiwantHttp.get<StaffItemListResponse>("/api/staff_page", {
    params: { search: "", category_id: 0, page: 1, limit: 50, ...params },
  });

  // 204 / 空响应兜底：本地 token 缺失时后端可能返回 204，兼容处理
  if (res.data.status === 204 || res.data.status === 401) {
    console.warn("[API] staff_page 返回", res.data.status, "→ 视为空列表");
    return { list: [], count: 0 };
  }

  if (res.data.status !== 200) {
    throw new Error(res.data.msg || "获取达人列表失败");
  }

  return {
    list: res.data.data.list.map(normalizeStaffItem),
    count: res.data.data.count,
  };
}

// ==================== 达人服务项目 ====================

/** 达人服务项目项 */
export interface StaffServiceItem {
  id: number;
  title: string;           // 项目名称
  thumb: string;           // 项目图片
  price: string;           // 小时单价
  day_order_money: string; // 日单价
  accept_order_type: number; // 0不限 1只接小时 2只接日单
  unit_name: string;       // 小时单位名(小时)
  day_unit_name: string;   // 天单位名(天)
}

/**
 * 获取达人服务列表（C端）
 * GET https://peiwan.banly.com.cn/api/massage/staff_item?staff_id={id}&page=1&limit=15
 * 对应 Apipost: 移动用户端 > 达人 > 达人信息 > 获取服务列表
 */
export async function fetchStaffServiceItems(params: {
  staff_id: number | string;
  page?: number;
  limit?: number;
}): Promise<StaffServiceItem[]> {
  console.log("[API] 获取达人服务列表:", "/massage/staff_item", params);

  const res = await peiwantHttp.get<{
    status: number;
    msg: string;
    data: {
      list: StaffServiceItem[];
      count: number;
    };
  }>("/api/massage/staff_item", {
    params: { page: 1, limit: 15, ...params },
    _skipAuth: true,
  } as any);

  // 服务端可能返回 204 No Content（无服务项目）或空数据
  if (!res.data || res.status === 204 || res.data.status !== 200) {
    return [];
  }

  return res.data.data.list ?? [];
}
